import { useMutation, useQuery } from '@tanstack/react-query';

import {
    ChatConversationSummary,
    ChatMessageFeedbackRequest,
    ChatMessageRating,
    GUID,
    PersonOrg,
} from '@ndvr/customer-api';

import { CustomerApi } from 'API';

export const AiChatQueryKeys = {
    conversations: (personOrgGUID: GUID<PersonOrg>) => ['ai-chat', 'conversations', personOrgGUID] as const,
    messages: (conversationId: string) => ['ai-chat', 'conversations', conversationId, 'messages'] as const,
} as const;

export const useConversations = (personOrgGUID: GUID<PersonOrg> | null | undefined, enabled: boolean = true) =>
    useQuery({
        queryKey: personOrgGUID
            ? AiChatQueryKeys.conversations(personOrgGUID)
            : ['ai-chat', 'conversations', 'disabled'],
        enabled: !!personOrgGUID && enabled,
        queryFn: async () => {
            const [data] = await CustomerApi().listConversations(personOrgGUID!);
            return data ?? [];
        },
    });

export const fetchConversationMessages = async (conversationId: string) => {
    const [data] = await CustomerApi().getConversationMessages(conversationId);
    return data ?? [];
};

export const useConversationMessages = (conversationId: string | null) =>
    useQuery({
        queryKey: conversationId ? AiChatQueryKeys.messages(conversationId) : ['ai-chat', 'messages', 'disabled'],
        enabled: !!conversationId,
        queryFn: () => fetchConversationMessages(conversationId!),
    });

interface SubmitFeedbackArgs {
    chatConversationGUID: GUID<ChatConversationSummary>;
    request: ChatMessageFeedbackRequest;
}

export const useSubmitFeedback = () =>
    useMutation({
        mutationFn: async ({ chatConversationGUID, request }: SubmitFeedbackArgs) => {
            await CustomerApi({ hideError: true }).submitMessageFeedback(chatConversationGUID, request);
        },
    });

export { ChatMessageRating };
